import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { config } from '../../shared/config';
import { CookieService } from 'ngx-cookie-service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { tmAuthConfig } from '../models/tm-auth-config.model';
import { ErrorStateService } from '../../shared/services/core/error.service';
import { LoggingService } from '../../shared/services/core/logging.service';
import { map, catchError } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import { ApiService } from '../../shared/services/core/api.service';
import { SocialLink } from '../models/socialLink.model';
import { ProviderName } from '../../account/models/login-response.model';
import { AuthService } from '../../user/services/auth.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class TMAuthService {
  constructor(
    private http: HttpClient,
    private cookie: CookieService,
    private errorService: ErrorStateService,
    public jwtHelper: JwtHelperService,
    public logService: LoggingService,
    private api: ApiService,
    private authService: AuthService,
    private router: Router,
    private tmConfig: tmAuthConfig
  ) {
    this.tmConfig = Object.assign({} as tmAuthConfig, new tmAuthConfig(), tmConfig);
  }

  private windowObjectReference: Window;

  /**
   * Attempt to login using tm auth
   *
   * @param additionalState any additional information you want returned
   */
  login(additionalState: string = ''): Promise<unknown> {
    let state = this._createNonce(16) + additionalState;
    let isHttps = false;

    if (this.tmConfig.authRedirectUri && this.tmConfig.authRedirectUri.toUpperCase().indexOf('HTTPS') != -1) {
      isHttps = true;
    }

    this.cookie.set('tm-state', state, undefined, '/', undefined, isHttps, undefined);

    let url =
      this.tmConfig.authUri +
      '?client_id=' +
      this.tmConfig.clientId +
      '&response_type=&scope=' +
      this.tmConfig.scope +
      '&redirect_uri=' +
      this.tmConfig.authRedirectUri +
      '&visualPresets=' +
      this.tmConfig.visualPresents +
      '&lang=en-us&placementId=' +
      this.tmConfig.placementId +
      '&integratorId=' +
      this.tmConfig.integratorId +
      '&intSiteToken=tm1-us&hideLeftPanel=false&state=' +
      state;

    this.windowObjectReference = window.open(
      url,
      'oauth-tm-popup',
      'toolbar=no, menubar=no, width=600, height=700, top=100, left=100'
    )!;
    this.windowObjectReference.focus();

    let authPromise = new Promise((resolve, reject) => {
      const eventMessageFunction = (e: MessageEvent) => {
        if (config.portalUrl.toUpperCase().indexOf(e.origin.toUpperCase()) == -1) {
          this.logService.info('mismatch in urls', config.portalUrl, e.origin);
          return;
        }

        if (e.data) {
          resolve(e.data);
          this.windowObjectReference.close();
        } else {
          this.windowObjectReference.close();
          reject('Unauthorised');
        }
      };
      window.addEventListener('message', eventMessageFunction, true);
    });

    return authPromise;
  }

  /**
   * Attempt to login using tm auth using redirect process
   *
   * @param additionalState any additional information you want returned
   */
  loginRedirect(additionalState: string = '') {
    let state = this._createNonce(16) + additionalState;
    let isHttps = false;

    if (this.tmConfig.authRedirectUri && this.tmConfig.authRedirectUri.toUpperCase().indexOf('HTTPS') != -1) {
      isHttps = true;
    }

    this.cookie.set('tm-state', state, undefined, '/', undefined, isHttps, undefined);

    let url =
      this.tmConfig.authUri +
      '?client_id=' +
      this.tmConfig.clientId +
      '&response_type=&scope=' +
      this.tmConfig.scope +
      '&redirect_uri=' +
      this.tmConfig.authRedirectUri +
      '&visualPresets=' +
      this.tmConfig.visualPresents +
      '&lang=en-us&placementId=' +
      this.tmConfig.placementId +
      '&integratorId=' +
      this.tmConfig.integratorId +
      '&intSiteToken=tm1-us&hideLeftPanel=false&state=' +
      state;

    window.location.href = url;
  }

  /**
   * SSO to modern account
   *
   * @param clubId ClubId defaulted to 1
   */
  ssoToModernAccount(clubId: number = 1) {
    let state = this._createNonce(16);
    let isHttps = false;

    if (this.tmConfig.authRedirectUri && this.tmConfig.authRedirectUri.toUpperCase().indexOf('HTTPS') != -1) {
      isHttps = true;
    }
    this._getToken()
      .pipe(
        map((x) => {
          if (x != null) {
            let headers = new HttpHeaders({
              'Content-Type': 'application/json',
            });

            this.http
              .post<any>(config.baseApiUrl + '/api/sso/oauth/refresh/2/' + x.Key1 + '/' + clubId, null, { headers: headers })
              .subscribe(
                (data: any) => {
                  window.location.href = this.tmConfig.loginUri + '?accessToken=' + data.AccessToken + '&redir=myevents';
                },
                (errorException: any) => {
                  this.logService.debug(errorException);
                  let errorMessage = 'unable to complete login';
                  if (errorException.message) {
                    errorMessage = errorException.message;
                  }
                  this.errorService.addError(errorMessage);
                }
              );
          } else {
            // show login
            this.cookie.set('tm-state', state, undefined, '/', undefined, isHttps, undefined);

            let url =
              this.tmConfig.authUri +
              '?client_id=' +
              this.tmConfig.clientId +
              '&response_type=&scope=' +
              this.tmConfig.scope +
              '&redirect_uri=' +
              this.tmConfig.authRedirectUri +
              '&visualPresets=' +
              this.tmConfig.visualPresents +
              '&lang=en-us&placementId=' +
              this.tmConfig.placementId +
              '&integratorId=' +
              this.tmConfig.integratorId +
              '&intSiteToken=tm1-us&hideLeftPanel=false&state=' +
              state;
            this.windowObjectReference = window.open(
              url,
              'oauth-tm-popup',
              'toolbar=no, menubar=no, width=600, height=700, top=100, left=100'
            )!;
            this.windowObjectReference.focus();

            const eventer = window.addEventListener;

            new Promise((resolve, reject) => {
              eventer(
                'message',
                (e) => {
                  if (config.portalUrl.toUpperCase().indexOf(e.origin.toUpperCase()) == -1) {
                    this.logService.info('mismatch in urls', config.portalUrl, e.origin);
                    return;
                  }

                  if (e.data) {
                    resolve(e.data);
                    this.windowObjectReference.close();
                    window.location.href = this.tmConfig.loginUri + '?accessToken=' + e.data.auth.AccessToken + '&redir=myevents';
                  } else {
                    this.windowObjectReference.close();
                    reject('Unauthorised');
                  }
                },
                false
              );
            });
          }
        }),
        catchError((err) => {
          this.cookie.set('tm-state', state, undefined, '/', undefined, isHttps, undefined);

          let url =
            this.tmConfig.authUri +
            '?client_id=' +
            this.tmConfig.clientId +
            '&response_type=&scope=' +
            this.tmConfig.scope +
            '&redirect_uri=' +
            this.tmConfig.authRedirectUri +
            '&visualPresets=' +
            this.tmConfig.visualPresents +
            '&lang=en-us&placementId=' +
            this.tmConfig.placementId +
            '&integratorId=' +
            this.tmConfig.integratorId +
            '&intSiteToken=tm1-us&hideLeftPanel=false&state=' +
            state;
          this.windowObjectReference = window.open(
            url,
            'oauth-tm-popup',
            'toolbar=no, menubar=no, width=600, height=700, top=100, left=100'
          )!;
          this.windowObjectReference.focus();

          const eventer = window.addEventListener;

          new Promise((resolve, reject) => {
            eventer(
              'message',
              (e) => {
                if (config.portalUrl.toUpperCase().indexOf(e.origin.toUpperCase()) == -1) {
                  this.logService.info('mismatch in urls', config.portalUrl, e.origin);
                  return;
                }

                if (e.data) {
                  resolve(e.data);
                  this.windowObjectReference.close();
                  window.location.href = this.tmConfig.loginUri + '?accessToken=' + e.data.auth.AccessToken + '&redir=myevents';
                } else {
                  this.windowObjectReference.close();
                  reject('Unauthorised');
                }
              },
              false
            );
          });
          return of([]);
        })
      )
      .toPromise();
  }

  /**
   * SSO to modern account redirect
   *
   * @param clubId ClubId defaulted to 1
   */
  ssoToModernAccountRedirect(clubId: number = 1) {
    let state = this._createNonce(16) + 'O';
    let isHttps = false;

    if (this.tmConfig.authRedirectUri && this.tmConfig.authRedirectUri.toUpperCase().indexOf('HTTPS') != -1) {
      isHttps = true;
    }
    this._getToken()
      .pipe(
        map((x) => {
          if (x != null) {
            let headers = new HttpHeaders({
              'Content-Type': 'application/json',
            });

            this.http
              .post<any>(config.baseApiUrl + '/api/sso/oauth/refresh/2/' + x.Key1 + '/' + clubId, null, { headers: headers })
              .subscribe(
                (data: any) => {
                  window.location.href = this.tmConfig.loginUri + '?accessToken=' + data.AccessToken + '&redir=myevents';
                },
                (errorException: any) => {
                  this.logService.debug(errorException);
                  let errorMessage = 'unable to complete login';
                  if (errorException.message) {
                    errorMessage = errorException.message;
                  }
                  this.errorService.addError(errorMessage);
                }
              );
          } else {
            // show login
            this.cookie.set('tm-state', state, undefined, '/', undefined, isHttps, undefined);

            let url =
              this.tmConfig.authUri +
              '?client_id=' +
              this.tmConfig.clientId +
              '&response_type=&scope=' +
              this.tmConfig.scope +
              '&redirect_uri=' +
              this.tmConfig.authRedirectUri +
              '&visualPresets=' +
              this.tmConfig.visualPresents +
              '&lang=en-us&placementId=' +
              this.tmConfig.placementId +
              '&integratorId=' +
              this.tmConfig.integratorId +
              '&intSiteToken=tm1-us&hideLeftPanel=false&state=' +
              state;
            window.location.href = url;
          }
        }),
        catchError((err) => {
          this.cookie.set('tm-state', state, undefined, '/', undefined, isHttps, undefined);
          let url =
            this.tmConfig.authUri +
            '?client_id=' +
            this.tmConfig.clientId +
            '&response_type=&scope=' +
            this.tmConfig.scope +
            '&redirect_uri=' +
            this.tmConfig.authRedirectUri +
            '&visualPresets=' +
            this.tmConfig.visualPresents +
            '&lang=en-us&placementId=' +
            this.tmConfig.placementId +
            '&integratorId=' +
            this.tmConfig.integratorId +
            '&intSiteToken=tm1-us&hideLeftPanel=false&state=' +
            state;
          window.location.href = url;

          return of([]);
        })
      )
      .toPromise();
  }

  /**
   * Log out of the portal and provider (TM).
   * Passes provider tokens and provider ID
   * to logout for the logout endpoint
   * with portal provider details.
   */
  logout() {
    let providerTokens: { [name: string]: string | null } = {};
    providerTokens.accessToken = localStorage.getItem('TMAccessToken');
    localStorage.removeItem('TMRefreshToken');
    localStorage.removeItem('TMAccessToken');

    return this.authService.logout(ProviderName.TicketMaster, providerTokens);
  }

  /**
   * Will process the auth response
   *
   * @param code the code from the authentication process
   * @param state the state that is sent in the inital request
   */
  authCallback(code: string, state: string, clubId: number = 1) {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    let openerLocation: string = window.opener?.location.href ?? '';

    let callbackSubscription = this.http
      .post<any>(config.baseApiUrl + '/api/sso/oauth/2/' + code + '/' + clubId, null, { headers: headers })
      .subscribe(
        (data) => {
          let cookieState = this.cookie.get('tm-state');

          if (state !== cookieState) {
            window.opener?.postMessage({ error: 'invalid state' }, openerLocation);
          } else {
            window.opener?.postMessage(data, openerLocation);
          }
          // set provider access and refresh token in local storage
          localStorage.setItem('TMRefreshToken', data.auth.RefreshToken.toString());
          localStorage.setItem('TMAccessToken', data.auth.AccessToken.toString());
          callbackSubscription.unsubscribe();
        },
        (errorException) => {
          let errorMessage = 'unable to complete callback';
          if (errorException.message) {
            errorMessage = errorException.message;
          }
          this.logService.debug(errorException);
          this.errorService.addError(errorMessage);
          window.opener?.postMessage({ error: errorMessage }, openerLocation);
          callbackSubscription.unsubscribe();
        }
      );
  }

  /**
   * Will process the auth response
   *
   * Direction 0 and 1 for Login, 2 for Registration
   *
   * @param code the code from the authentication process
   * @param state the state that is sent in the inital request
   */
  authCallbackRedirect(
    code: string,
    state: string,
    clubId: number = 1,
    redirectUrl: string = '/',
    registrationUrl: string = '/register/provideroauth',
    chooseRegUrl: string = '/login'
  ) {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    let cookieState = this.cookie.get('tm-state');
    let stateSplit = state.split('_');
    let direction = 0;

    if (stateSplit.length > 1) {
      if (stateSplit[1] === 'O') {
        direction = 1;
      } else if (stateSplit[1] === 'R') {
        direction = 2;
      }
    }

    let callbackSubscription = this.http
      .post<any>(config.baseApiUrl + '/api/sso/oauth/2/' + code + '/' + clubId, null, { headers: headers })
      .subscribe(
        (data) => {
          if (state !== cookieState) {
            this.router.navigate(['/login']);
            this.logService.debug('invalid state');
          } else {
            if (direction == 0) {
              // login page
              if (data.tokens && data.tokens.AccessToken) {
                // set provider access and refresh token in local storage
                localStorage.setItem('TMRefreshToken', data.auth.RefreshToken.toString());
                localStorage.setItem('TMAccessToken', data.auth.AccessToken.toString());
                callbackSubscription.unsubscribe();
                this.authService.loginUsingToken(data.tokens.AccessToken, data.tokens.RefreshToken, redirectUrl);
              } else {
                // User doesn't exist within fortress, redirect to chooseRegUrl
                callbackSubscription.unsubscribe();
                this.router.navigate([chooseRegUrl]);
              }
            } else if (direction == 2) {
              // registration page
              callbackSubscription.unsubscribe();
              this.router.navigateByUrl(registrationUrl, { state: { AccountNumber: data.auth.AccountNumber } });
            } else {
              // to mordern account
              if (data.tokens && data.tokens.AccessToken) {
                callbackSubscription.unsubscribe();
                window.location.href = this.tmConfig.loginUri + '?accessToken=' + data.auth.AccessToken + '&redir=myevents';
              } else {
                callbackSubscription.unsubscribe();
                this.router.navigate(['/']);
                this.logService.debug('not valid token');
              }
            }
          }
        },
        (errorException) => {
          let errorMessage = 'unable to complete callback';
          if (errorException.message) {
            errorMessage = errorException.message;
          }
          this.logService.debug(errorException);
          this.errorService.addError(errorMessage);
          callbackSubscription.unsubscribe();
          if (direction == 0) {
            this.router.navigate(['/login']);
          } else {
            this.router.navigate(['/']);
          }
        }
      );
  }

  /**
   * will return a tm connections from the api
   */
  private _getToken(): Observable<SocialLink> {
    return this.api.getAnySingle<SocialLink>('social/' + ProviderName.TicketMaster);
  }

  /**
   * Create a nonce for the state
   *
   * @param length length of the nonce
   */
  private _createNonce(length: number) {
    let charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-.';
    let result = '';

    while (length > 0) {
      var bytes = new Uint8Array(16);
      var random = window.crypto.getRandomValues(bytes);

      random.forEach(function (c) {
        if (length == 0) {
          return;
        }
        if (c < charset.length) {
          result += charset[c];
          length--;
        }
      });
    }
    return result + '_';
  }
}
